/**
 * Pitch-shift AudioNode wrapper using Signalsmith Stretch (WASM).
 *
 * Signalsmith Stretch is a high-quality WASM-based AudioWorklet for
 * polyphonic pitch shifting. It processes live audio input and shifts
 * pitch via semitones.
 *
 * This class wraps the signalsmith-stretch npm package behind a simple
 * interface used by AudioPlayer.
 *
 * @see https://signalsmith-audio.co.uk/code/stretch/
 */

// signalsmith-stretch is an ESM module that exports a factory function.
// The factory takes an AudioContext and returns a Promise<AudioWorkletNode>
// with extra methods (.schedule, .start, .stop, .configure, .latency, etc.)
//
// For live input mode (our use case), we just connect audio into it and
// use .schedule() to control pitch shift in semitones.

/** Type for the signalsmith-stretch AudioNode with its extra methods. */
interface SignalsmithStretchNode extends AudioWorkletNode {
  schedule(obj: Record<string, unknown>): Promise<unknown>;
  start(when?: number): Promise<unknown>;
  stop(when?: number): Promise<unknown>;
  configure(config: Record<string, unknown>): Promise<unknown>;
  latency(): Promise<number>;
}

/**
 * Wraps a Signalsmith Stretch AudioWorkletNode for pitch correction.
 *
 * Insert between audio source and destination to correct pitch changes
 * caused by playbackRate adjustments.
 *
 * Because the underlying signalsmith-stretch library requires async
 * initialisation (WASM loading, worklet registration), use the static
 * {@link PitchShiftNode.create} factory instead of calling `new` directly.
 *
 * @example
 * ```ts
 * const pitchShift = await PitchShiftNode.create(audioContext);
 * masterGain.connect(pitchShift.node);
 * pitchShift.node.connect(audioContext.destination);
 * pitchShift.setPitchFactor(1 / 1.5); // Correct for 1.5x playback rate
 * ```
 */
export class PitchShiftNode {
  private stretchNode: SignalsmithStretchNode;
  private audioContext: AudioContext;
  private latencySeconds = 0;
  private currentSemitones = 0;

  private constructor(
    stretchNode: SignalsmithStretchNode,
    audioContext: AudioContext,
  ) {
    this.stretchNode = stretchNode;
    this.audioContext = audioContext;
  }

  /**
   * Create and initialise a PitchShiftNode.
   *
   * Handles WASM loading, worklet registration, configuration and
   * starting the processor in live-input mode.
   *
   * @param audioContext - The AudioContext to use
   * @param channels - Number of output channels (default 2 for stereo)
   */
  static async create(
    audioContext: AudioContext,
    channels = 2,
  ): Promise<PitchShiftNode> {
    // Dynamic import since signalsmith-stretch is ESM-only
    const { default: SignalsmithStretch } = await import('signalsmith-stretch');

    const stretchNode: SignalsmithStretchNode = await SignalsmithStretch(
      audioContext,
      {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [channels],
      },
    );

    // Configure to match the official demo's settings for best quality.
    // - blockMs: 120ms STFT block size (longer = better frequency resolution)
    // - intervalMs: 30ms (blockMs/4 = 75% overlap, good balance of quality/CPU)
    // - splitComputation: spread STFT work evenly across render quanta to
    //   avoid bursty processing that can exceed the AudioWorklet deadline
    //
    // @see https://github.com/Signalsmith-Audio/signalsmith-stretch/blob/main/web/demo/index.html
    await stretchNode.configure({
      blockMs: 120,
      intervalMs: 30,
      splitComputation: true,
    });

    // Start processing live input immediately
    await stretchNode.start();

    const instance = new PitchShiftNode(stretchNode, audioContext);

    // Cache the node's latency so we can schedule pitch changes ahead of
    // time, giving the algorithm enough time to transition smoothly.
    const latency = await stretchNode.latency();
    instance.latencySeconds = latency;

    return instance;
  }

  /**
   * The underlying AudioWorkletNode.
   * Connect your audio source to this node's input,
   * and this node's output to the destination.
   */
  get node(): AudioWorkletNode {
    return this.stretchNode;
  }

  /**
   * Set the pitch shift in semitones.
   *
   * Positive values shift pitch up, negative values shift down.
   * For example:
   * - To correct for playbackRate R: semitones = -12 * log2(R)
   * - To transpose up 2 semitones: semitones = 2
   * - Combined: semitones = -12 * log2(R) + transpose
   *
   * @param semitones - Pitch shift in semitones
   */
  setSemitones(semitones: number): void {
    // Snap near-zero to exactly zero
    if (Math.abs(semitones) < 0.001) semitones = 0;

    // Avoid redundant schedule calls
    if (Math.abs(semitones - this.currentSemitones) < 0.01) return;
    this.currentSemitones = semitones;

    // Schedule the pitch change slightly ahead of time (at the node's
    // reported latency) so the algorithm can transition smoothly rather
    // than abruptly catching up.
    this.stretchNode.schedule({
      semitones,
      tonalityHz: 8000,
      formantBaseHz: 200,
      output: this.audioContext.currentTime + this.latencySeconds,
    });
  }

  /**
   * Disconnect and clean up.
   */
  destroy(): void {
    this.stretchNode.disconnect();
  }
}
